import React, { useState } from 'react';
import { DocumentIcon, TrashIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { ArrowUpTrayIcon } from '@heroicons/react/16/solid';
import { uploadDocument, deleteDocument } from '../api';
import { Document, Project } from '../types';

interface DocumentsProps {
    project: Project;
    onDocumentsChange?: () => void;
}

export default function Documents({ project, onDocumentsChange }: DocumentsProps) {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    };

    const getFileTypeColor = (fileType: string): string => {
        const type = fileType.toLowerCase();
        if (type === 'pdf') return 'bg-red-100 text-red-600';
        if (['doc', 'docx'].includes(type)) return 'bg-blue-100 text-blue-600';
        if (['xls', 'xlsx'].includes(type)) return 'bg-green-100 text-green-600';
        if (['jpg', 'jpeg', 'png', 'gif'].includes(type)) return 'bg-purple-100 text-purple-600';
        return 'bg-gray-100 text-gray-600';
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        setIsUploading(true);
        setUploadError(null);

        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                await uploadDocument(project.id, file, file.name);
            }
            if (onDocumentsChange) {
                onDocumentsChange();
            }
            // Reset the input
            event.target.value = '';
        } catch (error) {
            setUploadError(error instanceof Error ? error.message : 'Failed to upload document');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (documentId: number) => {
        if (!window.confirm('Are you sure you want to delete this document?')) {
            return;
        }

        setDeletingIds(prev => new Set(prev).add(documentId));
        try {
            await deleteDocument(documentId);
            if (onDocumentsChange) {
                onDocumentsChange();
            }
        } catch (error) {
            alert('Failed to delete document');
        } finally {
            setDeletingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(documentId);
                return newSet;
            });
        }
    };

    const handleDownload = (document: Document) => {
        window.open(document.file_url, '_blank');
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-xl font-bold text-pacificblue-950">Documents</h3>
                <label className="cursor-pointer">
                    <input
                        type="file"
                        multiple
                        onChange={handleFileUpload}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif"
                        disabled={isUploading}
                    />
                    <span className={`inline-flex items-center text-sm text-gray-500 ${
                        isUploading 
                            ? 'cursor-not-allowed' 
                            : 'hover:text-gray-700'
                    }`}>
                        {isUploading ? 'Uploading...' : 'Upload'}
                        <ArrowUpTrayIcon className="w-4 h-4 w-4 h-4 inline ml-1" />
                    </span>
                </label>
            </div>

            {uploadError && (
                <div className="mb-2 p-2 bg-red-100 text-red-700 rounded text-sm">
                    {uploadError}
                </div>
            )}

            <div className="bg-stone-200 rounded-2xl p-4">
                {project.documents && project.documents.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
                        {project.documents.map((document) => (
                            <div 
                                key={document.id} 
                                className="relative group flex flex-col bg-stone-100 rounded hover:bg-gray-100 p-3 transition-colors"
                            >
                                {/* Delete button */}
                                <button
                                    onClick={() => handleDelete(document.id)}
                                    disabled={deletingIds.has(document.id)}
                                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-white rounded-full shadow-md hover:bg-red-50 disabled:opacity-50"
                                    title="Delete document"
                                >
                                    <TrashIcon className="w-4 h-4 text-red-600" />
                                </button>

                                {/* File icon and download button */}
                                <div 
                                    onClick={() => handleDownload(document)}
                                    className="cursor-pointer flex flex-col items-center"
                                >
                                    <div className={`w-10 h-10 mb-2 ${getFileTypeColor(document.file_type)} rounded flex items-center justify-center text-xs font-bold`}>
                                        {document.file_type}
                                    </div>
                                    <div className="text-xs text-center w-full">
                                        <p className="font-semibold truncate" title={document.name}>
                                            {document.name}
                                        </p>
                                        <p className="text-gray-500 text-xxs mt-1">
                                            {formatDate(document.uploaded_at)}
                                        </p>
                                        <p className="text-gray-500 text-xxs">
                                            {formatFileSize(document.file_size)}
                                        </p>
                                        {document.uploaded_by_name && (
                                            <p className="text-gray-500 text-xxs mt-1 truncate" title={document.uploaded_by_name}>
                                                by {document.uploaded_by_name}
                                            </p>
                                        )}
                                    </div>
                                    <ArrowDownTrayIcon className="w-4 h-4 text-gray-400 mt-2" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <DocumentIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500 text-sm">No documents uploaded</p>
                        <p className="text-gray-400 text-xs mt-1">Click the Upload button to add documents</p>
                    </div>
                )}
            </div>
        </div>
    );
}